// [claude-code 2026-04-23] Rollback: strip GitHub OAuth token state and handlers
// [claude-code 2026-04-18] Notify the local backend of the signed-in user's sub so its relay
//   connector can register the outbound WS under the correct user_id. Without this the local
//   backend has no way to know which user it serves, Fly reports connected:false on
//   /api/relay/health, and mobile's ChatInput stays locked in "OFFLINE".
// [claude-code 2026-03-24] Supabase Google OAuth + Electron deep link
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import {
  defaultBillingStatus,
  defaultEntitlements,
  fetchBillingStatus,
  type BillingStatusResponse,
  type EntitlementKey,
} from "../lib/billing";
import { useActiveFeatureUsage } from "../lib/active-feature-usage";
import { useAuthBackendSync } from "./auth-backend-sync";
import {
  buildDesktopDeepLink,
  clearDesktopAuthRestoreIntent,
  hasRecentDesktopAuthRestoreIntent,
} from "../lib/desktop-auth-restore";
import { consumeForumInviteReturnUrl } from "../lib/forum-invite-deeplink";
import type { Session, User } from "@supabase/supabase-js";
import { useAuthSession } from "./use-auth-session";

export type UserTier = "free" | "fintheon" | "fintheon_plus" | "fintheon_pro";

interface AuthContextType {
  tier: UserTier;
  setTier: (tier: UserTier) => void;
  entitlements: Record<EntitlementKey, boolean>;
  billingStatus: BillingStatusResponse;
  refreshBillingStatus: () => Promise<void>;
  isAuthenticated: boolean;
  userId: string;
  isLoading: boolean;
  session: Session | null;
  user: User | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoading, session, signIn, signOut, user } = useAuthSession();
  const [tier, setTier] = useState<UserTier>("free");
  const [billingStatus, setBillingStatus] =
    useState<BillingStatusResponse>(defaultBillingStatus);

  useAuthBackendSync(user?.id ?? null, session?.access_token ?? null);
  useActiveFeatureUsage(Boolean(session?.access_token));

  useEffect(() => {
    if (!session) return;
    if (typeof window === "undefined") return;
    if (!window.electron) {
      const forumInviteReturnUrl = consumeForumInviteReturnUrl();
      if (
        forumInviteReturnUrl &&
        forumInviteReturnUrl !== window.location.href
      ) {
        const url = new URL(forumInviteReturnUrl);
        window.history.replaceState({}, "", `${url.pathname}${url.search}`);
      }
    }
  }, [session]);

  useEffect(() => {
    if (!session) return;
    if (typeof window === "undefined" || window.electron) return;
    if (!hasRecentDesktopAuthRestoreIntent()) return;

    const deepLink = buildDesktopDeepLink(session);
    if (!deepLink) return;

    clearDesktopAuthRestoreIntent();
    window.location.replace(deepLink);
  }, [session]);

  const refreshBillingStatus = useCallback(async () => {
    const token = session?.access_token ?? null;
    const status = await fetchBillingStatus(token).catch(
      () => defaultBillingStatus,
    );
    setBillingStatus(status);
    setTier(status.tier ?? "free");
  }, [session?.access_token]);

  useEffect(() => {
    void refreshBillingStatus();
  }, [refreshBillingStatus]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    return session?.access_token ?? null;
  }, [session?.access_token]);

  const isAuthenticated = session !== null;
  const userId = user?.id ?? "";

  return (
    <AuthContext.Provider
      value={{
        tier,
        setTier,
        entitlements: billingStatus.entitlements ?? defaultEntitlements,
        billingStatus,
        refreshBillingStatus,
        isAuthenticated,
        userId,
        isLoading,
        session,
        user,
        signIn,
        signOut,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
